import UAParser from 'ua-parser-js';

import type {MFAModes} from '@/enums/authentication';
import {LOGIN_URLS} from '@/utils/constants/api-urls';
import axios from '@/utils/helpers/axios';

const authApis = {
  createSession: async function createSession(
    browser: {
      name?: string;
      version?: string;
    },
    OS: {
      name?: string;
      version?: string;
    },
  ): Promise<{
    message: string;
  }> {
    return axios
      .post(LOGIN_URLS.CREATE_SESSION(), {
        browser,
        OS,
      })
      .then((res) => {
        return res.data;
      });
  },

  checkGLID: async function checkGLID(GLID: string): Promise<{
    data: {
      name: string;
      valid: boolean;
    };
    succeeded: boolean;
  }> {
    return axios.get(LOGIN_URLS.CHECK_GLID(GLID)).then((res) => {
      return res.data;
    });
  },

  checkGLIDhasPassword: async function checkGLIDhasPassword(GLID: string): Promise<{
    data: {
      status: boolean;
    };
    succeeded: boolean;
  }> {
    return axios.get(LOGIN_URLS.CHECK_GLID_HAS_PASSWORD(GLID)).then((res) => {
      return res.data;
    });
  },

  logIn: async function (
    glid: string,
    password: string,
  ): Promise<{
    guid: string;
    requiredMFAauth: boolean;
    status: string;
    verifiedMFAs: string[];
  }> {
    const res = await axios.post(LOGIN_URLS.PASSWORD_LOGIN(), {
      glid,
      password,
    });
    return res.data.data;
  },

  logInWithOTP: function (glid: string, mfaId: string, otp: string, authType: MFAModes) {
    return axios
      .post(LOGIN_URLS.LOGIN_WITH_MFA_OTP(), {
        glid,
        mfaId,
        otp,
        authType,
      })
      .then((res) => {
        return res.data.data;
      });
  },

  verifyLoginAuthRef: function (authRef: string) {
    return axios
      .post(LOGIN_URLS.VERIFY_LOGIN_AUTH_REF(), {
        authRef,
      })
      .then(async (res) => {
        return res.data.data;
      });
  },

  verifyMagicLink: function (authRef: string, guid: string, mode: string) {
    return axios
      .post(LOGIN_URLS.VERIFY_LOGIN_MAGIC_LINK(), {
        authRef,
        guid,
        mode,
      })
      .then(async (res) => {
        return res.data.data;
      });
  },

  checkSessionState: function () {
    return axios.get(LOGIN_URLS.CHECK_SESSION_STATE()).then((res) => {
      return res.data;
    });
  },

  sendMfaOTP: function (glid: string, mode: MFAModes) {
    return axios.get(LOGIN_URLS.SEND_MFA_OTP(glid, mode)).then((res) => {
      return res.data;
    });
  },

  recoverGlid: function (type: string, value: string) {
    return axios
      .post(LOGIN_URLS.RECOVER_GLID(), {
        type,
        value,
      })
      .then((res) => {
        return res.data.data;
      });
  },

  forgotPasswordMFA: function (glid: string, mfaType: string) {
    return axios
      .post(LOGIN_URLS.FORGOT_PASSWORD_MFA(), {
        glid,
        mfaType,
      })
      .then((res) => {
        return res.data;
      });
  },

  validateForgotPasswordMFA: function (glid: string, guid: string, otp: string) {
    return axios
      .post(LOGIN_URLS.VALIDATE_FORGOT_PASSWORD_MFA(), {
        glid,
        otp,
        guid,
      })
      .then((res) => {
        return res.data;
      });
  },

  resetPassword: function (password: string) {
    return axios
      .post(LOGIN_URLS.RESET_PASSWORD(), {
        password,
      })
      .then((res) => {
        return res.data;
      });
  },

  deletePassword: function () {
    return axios.post(LOGIN_URLS.DELETE_PASSWORD()).then((res) => {
      return res.data;
    });
  },

  getVerifiedMFAsByGLID: function (glid: string) {
    return axios.get(LOGIN_URLS.GET_VERIFIED_MFAS(glid)).then((res) => {
      return res.data;
    });
  },
};

export default authApis;

export function createSessionWithDeviceDetails() {
  const userDevice = UAParser();
  return authApis
    .createSession(userDevice.browser, userDevice.os)
    .then(() => {})
    .catch(() => {});
}
